import { Layout, Spin } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { AppwriteException } from 'appwrite';
import EnterFullScreen from 'assets/Icons/fullscreen/EnterFullScreen';
import ExitFullScreen from 'assets/Icons/fullscreen/ExitFullScreen';
import clsx from 'clsx';
import { motion, useAnimation } from 'framer-motion';
import moment, { Moment } from 'moment';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import { useRecoilState, useRecoilValue } from 'recoil';
import { UAParser } from 'ua-parser-js';

import ChapterDrawer from '@/components/PdfReader/ChapterDrawer';
import { Note } from '@/components/PdfReader/GeneratePdf/NotePdfDocument';
import PageNavigator from '@/components/PdfReader/PageNavigator';
import NotesApp from '@/components/PdfReader/PdfNotes/PdfNotes';
import SelectionTooltip from '@/components/PdfReader/PdfNotes/SelectionTooltip';
import TakeNotePanel from '@/components/PdfReader/PdfNotes/TakeNotePanel';
import ToolBoxSidebar from '@/components/PdfReader/ToolBoxSidebar';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { generateAndDownloadPdf } from '@/components/shared/generateAndDownloadPdf';

import { drawerPdfAtom } from '@/atoms/pdfStates/drawerAtom';
import { notesAtom } from '@/atoms/pdfStates/notesAtom';
import { takeNoteModalAtom } from '@/atoms/pdfStates/takeNoteModalAtom';

import AiChatDrawer from '../../PdfReader/AiChatDrawer';
import FailedToLoadPDF from '../../PdfReader/FailedToLoadPDF';
import PdfReaderHeader from '../../PdfReader/PdfReaderHeader';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { LoadingV2 } from '../../shared/LoadingV2';
import { FailedToLoadPdf, NetworkError } from '../../../../assets';
import { iframeEmail } from '../../../atoms/iframeEmail';
import { user } from '../../../atoms/user';
import appwriteService from '../../../config/appwrite';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { Endpoints } from '../../../network';
import {
  getBookByIdApiIFrame,
  tempUserIFrame,
} from '../../../services/IFrame/apis';
import {
  getAllChaptersApi,
  getBookByIdApi,
} from '../../../services/Library/Books';
import { MyBookByIDInterface } from '../../../utils/interfaces/Library/Books';
import { AllChaptersListInterface } from '../../../utils/interfaces/Library/Pdf';
import { User } from '../../../utils/interfaces/User';

interface RotationsState {
  [key: number]: number;
}

const { Content } = Layout;

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface Props {
  isIFrame?: boolean;
  noAuth?: boolean;
  theme?: string;
  isModal?: boolean;
  bookSlugId?: string;
  handleCancel?: () => void;
  isSampleReadingPag?: boolean;
}

const PDFReaderIframe: React.FC<Props> = ({
  isIFrame = false,
  noAuth = false,
  theme,
  isModal = false,
  bookSlugId,
}) => {
  const router = useRouter();
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotations, setRotations] = useState<RotationsState>({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [sideBySide, setSideBySide] = useState(true);
  const [visible, setVisible] = useState(true);
  const [zoomValue, setZoomValue] = useState(100);
  const [samplePdf, setSamplePdf] = useState<string>();
  const [, setVisibleControl] = useRecoilState(iframeEmail);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [takeNoteModal, setTakeNoteModal] = useRecoilState(takeNoteModalAtom);
  const drawerState = useRecoilValue(drawerPdfAtom);

  const [hasError, setHasError] = useState(false);
  const [timeStamp, setTimeStamps] = useState({
    startTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
    endTime: '',
  });
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );
  const notes = useRecoilValue<Record<string, Note[]>>(notesAtom);
  const controls = useAnimation();

  const [chapterFieldId, setChapterFieldId] = useState('');
  const [startPage, setStartPage] = useState<number>(0);
  const [chaptersList, setChaptersList] = useState<AllChaptersListInterface>();
  const [booksDetails, setBooksDetails] = useState<MyBookByIDInterface>();
  const [isInteracted, setIsInteracted] = useState(false);
  const [renderPdf, setRenderPdf] = useState('');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const userData: User | undefined = useRecoilValue(user);
  const containerRef = useRef<HTMLDivElement>(null);
  const baseUrl = process.env.NEXT_PUBLIC_API_READER_URL;
  const screens = useBreakpoint();
  const [errorType, setErrorType] = useState<
    'server-timeout' | 'default' | null
  >(null);
  const samplePdfValue = 'true';
  const leftButtonRef = useRef<HTMLButtonElement>(null);
  const rightButtonRef = useRef<HTMLButtonElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const pageNavigatorRef = useRef<HTMLDivElement>(null);

  const pageInputRef = useRef<HTMLInputElement>(null);
  const takeNotePanelRef = useRef<HTMLDivElement>(null);

  const {
    chapterPageNumber,
    chapterId,
    book_id,
    email,
    code: afc,
  } = router.query;

  const chapters = chaptersList?.chapters || [];
  const totalNumberOfPages = chapters.reduce(
    (total, chapter) => total + chapter.totalPages,
    0,
  );

  const fullScreenIcon = (
    <div className='group'>
      <button
        onClick={() => toggleFullScreen()}
        className={clsx(
          isFullScreen ? ' text-primary' : 'text-white ',
          'flex items-center  justify-center relative cursor-pointer g p-0   lg:p-2 w-6 h-6 md:w-10 md:h-10 rounded-full',
        )}
      >
        {!isFullScreen ? <EnterFullScreen /> : <ExitFullScreen />}
      </button>
    </div>
  );

  const extractId = (path: string | undefined): string | undefined => {
    if (path) {
      const parts = path.split('-');
      return parts[parts.length - 1];
    } else return undefined;
  };
  const onDocumentLoadSuccess = async ({ numPages }: { numPages: number }) => {
    try {
      if (!samplePdf && !renderPdf) {
        throw new Error('PDF file is null or undefined');
      }

      setNumPages(numPages);

      const pdf = await pdfjs.getDocument(samplePdf ?? renderPdf).promise;

      if (!pdf) {
        throw new Error('Failed to load PDF document');
      }

      const page = await pdf.getPage(1);
      const { width, height } = page.getViewport({ scale: 1 });
      setPageDimensions({ width, height });
    } catch (error) {
      console.error('Failed to load PDF:', error);
    }
  };

  const onCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const rotateLeft = (pageNumber: number) => {
    setRotations((prevRotations) => ({
      ...prevRotations,
      [pageNumber]: (prevRotations[pageNumber] || 0) - 90,
    }));
  };

  const rotateRight = (pageNumber: number) => {
    setRotations((prevRotations) => ({
      ...prevRotations,
      [pageNumber]: (prevRotations[pageNumber] || 0) + 90,
    }));
  };

  const handleDownload = () => {
    generateAndDownloadPdf({
      notes: Object.values(notes)
        .flat()
        .map((note) => ({
          ...note,
        })),
      title: booksDetails?.title || 'Zebralearn Book Notes',
      author:
        booksDetails?.author_list?.map((author) => author.name).join(', ') ||
        'Zebralearn',
      landingPageUrl: `${
        process.env.NEXT_PUBLIC_APP_ENV === 'production'
          ? 'https://zebralearn.com'
          : 'https://staging.zebralearn.com'
      }/books/${booksDetails?.slug}-${book_id}` as string,
    });
    toggleFullScreen();
  };

  const getNextPage = async () => {
    if (pageNumber + 1 <= (numPages || 1)) {
      if (sideBySide) {
        if ((numPages || 0) < pageNumber + 1 + 2 && !samplePdf) {
          return numPages;
        } else {
          return pageNumber + 2;
        }
      } else {
        return pageNumber + 1;
      }
    }

    return pageNumber;
  };
  const showDrawer = () => {
    setVisible(!visible);
  };

  const useCurrentChapter = useMemo(() => {
    return (
      chaptersList?.chapters?.find(
        (chapter) =>
          chapter &&
          chapter?.endPage &&
          pageNumber >= chapter.startPage &&
          pageNumber <= chapter?.endPage,
      ) || null
    );
  }, [pageNumber, book_id, takeNoteModal.value]);
  const onChapterHandler = async (chapterId: string, startPage?: number) => {
    setChapterFieldId(chapterId);
    setStartPage(startPage as number);
    setPageNumber(startPage as number);
  };
  const isIphone = (): boolean => {
    const parser = new UAParser();
    const device = parser.getDevice();
    return device.model === 'iPhone';
  };

  const onClose = () => {
    setVisible(false);
  };

  const getPreviousPage = async () => {
    if (pageNumber > 1) {
      return sideBySide ? pageNumber - 2 : pageNumber - 1;
    }

    const currentIndex = chaptersList?.chapters.findIndex(
      (item: any) => item._id === chapterFieldId,
    );

    if (1 === pageNumber && currentIndex !== undefined && currentIndex !== -1) {
      if (currentIndex - 1 >= 0) {
        const prevChapter = chaptersList?.chapters?.[currentIndex - 1];

        if (prevChapter) {
          setRenderPdf(
            `${baseUrl}/${Endpoints.getChapterFile}/${prevChapter._id}`,
          );

          setScale(1.0);
          setZoomValue(100);

          setChapterFieldId(prevChapter?._id as string);
          setStartPage(prevChapter?.startPage);

          setPageNumber(prevChapter?.totalPages - 1);
          return prevChapter?.totalPages;
        } else {
          console.log('No previous chapter available.');
        }
      } else {
        console.log('No previous chapter available.');
      }
    } else {
      console.log('No previous chapter available.');
    }

    return pageNumber;
  };

  const handlePageTransition = async (newPageNumber: number) => {
    if (newPageNumber >= 1 && newPageNumber <= (numPages || 1)) {
      setPageNumber(newPageNumber);
    }
  };

  const zoomIn = () => {
    if (scale < 5.0) {
      const newScale = scale + 0.1;
      handleZoomChange(newScale);
    }
  };

  const zoomOut = () => {
    const newScale = scale - 0.1;
    handleZoomChange(newScale);
  };

  const handleGetDefaultBookById = async (chapterIdUrl: string) => {
    try {
      setRenderPdf(
        `${baseUrl}/${Endpoints.getChapterFile}/${chapterIdUrl as string}`,
      );

      setScale(1.0);
      setZoomValue(100);
      setHasError(false);
      setErrorType(null);
    } catch (error: any) {
      setHasError(true);

      if (
        error.code === 'ECONNABORTED' ||
        error.code === 'ECONNRESET' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.toLowerCase().includes('timeout') ||
        error.message?.toLowerCase().includes('network') ||
        error.message?.toLowerCase().includes('fetch')
      ) {
        setErrorType('server-timeout');
      } else {
        setErrorType('default');
      }
    }
  };

  const toggleFullScreen = () => {
    const layout = document.querySelector('.ant-layout-pdf') as any;

    if (layout) {
      if (!isFullScreen) {
        const fullscreenFunction =
          layout.requestFullscreen ||
          layout.webkitRequestFullscreen ||
          layout.msRequestFullscreen ||
          layout.mozRequestFullScreen;

        if (fullscreenFunction) {
          try {
            const orientation = screen.orientation as any;
            if (orientation && orientation.lock) {
              orientation.lock('portrait').catch((error: any) => {
                console.error('Failed to lock orientation:', error);
              });
            }

            // Using promise-based fullscreen API if available
            if (fullscreenFunction === layout.requestFullscreen) {
              fullscreenFunction.call(layout).catch((error: any) => {
                console.error('Failed to enter fullscreen mode:', error);
              });
            } else {
              fullscreenFunction.call(layout);
            }
          } catch (error) {
            console.error('Failed to enter fullscreen mode:', error);
          }
        } else {
          console.warn('Fullscreen API is not supported by this browser.');
        }
      } else {
        const docs = document as any;
        const exitFullscreenFunction =
          docs.exitFullscreen ||
          docs.webkitExitFullscreen ||
          docs.msExitFullscreen ||
          docs.mozCancelFullScreen;

        if (exitFullscreenFunction) {
          try {
            // Using promise-based exit fullscreen API if available
            if (exitFullscreenFunction === docs.exitFullscreen) {
              exitFullscreenFunction.call(document).catch((error: any) => {
                console.error('Failed to exit fullscreen mode:', error);
              });
            } else {
              exitFullscreenFunction.call(document);
            }

            setIsFullScreen(!isFullScreen);
          } catch (error) {
            console.error('Failed to exit fullscreen mode:', error);
          }
        } else {
          console.warn('Exit fullscreen API is not supported by this browser.');
        }
      }
    } else {
      console.warn('Layout element not found.');
    }
  };

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      try {
        if (event.key === 'ArrowLeft' && !openDrawer) {
          const previousPage = await getPreviousPage();
          handlePageTransition(previousPage);
          if (leftButtonRef.current) {
            leftButtonRef.current.click();
          }
        } else if (event.key === 'ArrowRight' && !openDrawer) {
          if (!(pageNumber >= 10)) {
            const nextPage = await getNextPage();
            handlePageTransition(nextPage as number);

            // Optional: focus on the right button when arrow right is pressed
            if (rightButtonRef.current) {
              rightButtonRef.current.click();
            }
          }
        }
      } catch (error) {
        console.error('Error while handling keyboard input:', error);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(
        !!document.fullscreenElement ||
          !!(document as any).webkitFullscreenElement ||
          !!(document as any).msFullscreenElement ||
          !!(document as any).mozFullScreenElement,
      );
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullScreenChange,
      );
      document.removeEventListener(
        'msfullscreenchange',
        handleFullScreenChange,
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullScreenChange,
      );
    };
  }, []);

  const toggleSideBySide = () => {
    setSideBySide(!sideBySide);
  };
  const { bookId } = router.query;

  const parsedBookId = bookId ? (bookId as string).split('-').pop() || '' : '';

  const getAllChaptersListData = async (book_id: string) => {
    try {
      const response = await getAllChaptersApi(book_id);
      setChaptersList(response);

      if (!(chapterPageNumber && chapterId)) {
        setRenderPdf(
          `${baseUrl}/${Endpoints.getChapterFile}/${response?.chapters[0]?._id}`,
        );
        setScale(1.0);
        setZoomValue(100);

        setChapterFieldId(response?.chapters[0]?._id as string);
        setStartPage(response?.chapters[0]?.startPage);
      } else {
        setStartPage(
          response?.chapters.find((chapter: any) => chapter._id === chapterId)
            ?.startPage as number,
        );
        if (chapterPageNumber && response?.chapters) {
          setPageNumber(
            Number(chapterPageNumber) -
              (response?.chapters?.find(
                (chapter: any) => chapter._id === chapterId,
              )?.startPage || 0) +
              1,
          );
        }
      }
    } catch (error) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };
  const isLandscapeMode = useMemo(() => {
    return pageDimensions.width > pageDimensions.height;
  }, [pageDimensions]);

  const getBookDetailsById = async (bookId: string) => {
    try {
      if (isIFrame) {
        const response = await getBookByIdApiIFrame(bookId);

        setBooksDetails(response);
      } else {
        const response = await getBookByIdApi(bookId);
        setBooksDetails(response);
      }
    } catch (error) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handleGetSampleBookById = async (bookId: string) => {
    try {
      setSamplePdf(`${baseUrl}/${Endpoints.getSampleBook}/${bookId}`);
      if (chapterPageNumber) {
        setPageNumber(+chapterPageNumber);
      }
    } catch (error: any) {
      setHasError(true);
      if (
        error.code === 'ECONNABORTED' ||
        error.code === 'ECONNRESET' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.toLowerCase().includes('timeout') ||
        error.message?.toLowerCase().includes('network') ||
        error.message?.toLowerCase().includes('fetch')
      ) {
        setErrorType('server-timeout');
      } else {
        setErrorType('default');
      }
    }
  };

  const handleGetDefaultBookByChapterId = async () => {
    try {
      if (chapterId) {
        handleGetDefaultBookById(chapterId as string);
        setChapterFieldId(chapterId as string);
      }
    } catch (error) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  useEffect(() => {
    if (chapterPageNumber && chapterId) {
      getAllChaptersListData((book_id ?? parsedBookId) as string);
      handleGetDefaultBookByChapterId();

      getBookDetailsById((book_id ?? parsedBookId) as string);
    } else if (samplePdfValue && ((book_id ?? parsedBookId) as string)) {
      handleGetSampleBookById((book_id ?? parsedBookId) as string);
      getBookDetailsById(extractId(bookSlugId) as string);
    } else if (samplePdfValue && extractId(bookSlugId)) {
      handleGetSampleBookById(extractId(bookSlugId) as string);
      getBookDetailsById(extractId(bookSlugId) as string);
    } else if ((book_id ?? parsedBookId) as string) {
      getAllChaptersListData((book_id ?? parsedBookId) as string);
      getBookDetailsById((book_id ?? parsedBookId) as string);
    }
  }, [
    book_id,
    parsedBookId,
    samplePdfValue,
    chapterPageNumber,
    chapterId,
    bookSlugId,
  ]);

  const handleReadingLogs = async () => {
    try {
      const duration = moment().diff(timeStamp.startTime, 'milliseconds');

      const baseEventParams = {
        event_name: 'read',
        source: 'books',
        source_type: router.query['cs']
          ? 'comingSoon'
          : router.query['swp']
            ? 'short'
            : 'long',
        source_id: extractId(bookSlugId) as string,
        sub_source: samplePdf ? 'sample' : 'chapter',
        sub_source_id: samplePdf ? null : chapterFieldId,
        source_name: booksDetails?.title,
        meta: {
          book_page_no: samplePdf ? pageNumber : startPage + pageNumber - 1,
          book_total_pages: samplePdf ? numPages : totalNumberOfPages,
          ...(!samplePdf && { chapter_page_no: pageNumber }),
        },
      };

      const logReadEvent = (
        start: Moment,
        end: Moment,
        meta: Record<string, any>,
      ) => {
        logEvent({
          eventDetails: {
            ...baseEventParams,
            activity_start: start.format('YYYY-MM-DDTHH:mm:ss'),
            activity_end: end.format('YYYY-MM-DDTHH:mm:ss'),
            meta,
          },
          userData,
          pathname: router.pathname,
        });
      };

      if (sideBySide) {
        const halfDuration = duration / 2;
        const firstEventEndTime = moment(timeStamp.startTime)
          .clone()
          .add(halfDuration, 'milliseconds');
        const firstMeta = {
          book_page_no: samplePdf ? pageNumber - 2 : startPage + pageNumber - 3,
          ...(!samplePdf && { chapter_page_no: pageNumber - 2 }),
        };

        const secondMeta = {
          book_page_no: samplePdf ? pageNumber - 1 : startPage + pageNumber - 2,
          ...(!samplePdf && { chapter_page_no: pageNumber - 1 }),
        };
        logReadEvent(moment(timeStamp.startTime), firstEventEndTime, firstMeta);
        logReadEvent(firstEventEndTime, moment(), secondMeta);
      } else {
        const meta = {
          book_page_no: samplePdf ? pageNumber - 1 : startPage + pageNumber - 2,
          ...(!samplePdf && { chapter_page_no: pageNumber - 1 }),
        };
        logReadEvent(moment(timeStamp.startTime), moment(), meta);
      }

      setTimeStamps({
        startTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
        endTime: '',
      });
    } catch (error) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  useEffect(() => {
    if (chapterFieldId || samplePdf) {
      handleReadingLogs();
    }
    if (
      (samplePdf ? pageNumber : startPage + pageNumber - 1) ===
        (samplePdf ? numPages : totalNumberOfPages) &&
      (samplePdf ? pageNumber : startPage + pageNumber - 1) > 0
    ) {
      const eventParams: EventDetails = {
        event_name: 'complete',
        source: 'books',
        source_type: router.query['cs']
          ? 'comingSoon'
          : router.query['swp']
            ? 'short'
            : samplePdf
              ? 'short'
              : 'long',
        source_id: extractId(bookSlugId) as string,
        sub_source: samplePdf ? 'sample' : null,
        sub_source_id: samplePdf ? null : chapterFieldId,
        unit: 'boolean',
        source_name: booksDetails?.title,
        value: 1,
        meta: {
          book_page_no: samplePdf ? pageNumber : startPage + pageNumber - 1,
          book_total_pages: samplePdf ? numPages : totalNumberOfPages,
          ...(!samplePdf && { chapter_page_no: pageNumber }),
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });
    }
  }, [pageNumber]);

  const handleZoomChange = (newScale: any) => {
    setScale(newScale);
    setZoomValue(Math.round(newScale * 100));
  };

  useEffect(() => {
    if (!screens.lg) {
      setSideBySide(false);
    } else {
      setSideBySide(true);
    }
  }, [screens]);

  useEffect(() => {
    if (numPages && pageNumber + 1 > (numPages as number) && sideBySide) {
      setSideBySide(false);
    }
  }, [
    pageNumber,
    startPage,
    totalNumberOfPages,
    sideBySide,
    numPages,
    screens,
    samplePdfValue,
    pageDimensions,
  ]);

  useEffect(() => {
    const handleTouchStart = () => {
      setIsInteracted(true);
    };

    const handleTouchMove = () => {
      setIsInteracted(true);
    };

    const handleTouchEnd = () => {
      setIsInteracted(true);
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsInteracted(false);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [isInteracted]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      setWindowHeight(container.clientHeight);
      setWindowWidth(container.clientWidth);
    }
  }, []);

  const handleEmailLoginTempIFrame = async (emailValue: string) => {
    try {
      await appwriteService.logoutIfSessionExists();
      const session = await appwriteService.createAnonymousSession();
      if (session && typeof window !== undefined) {
        await tempUserIFrame({
          code: afc as string,
          email: emailValue as string,
          product_id: book_id as string,
          source: window.location.href,
          user_id: session?.userId,
        });
      }
      if (session?.userId && typeof localStorage !== 'undefined') {
        localStorage.setItem('user_id_iframe', session?.userId);
      }
    } catch (error: any) {
      if (error instanceof AppwriteException) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error initializing messaging:', error);
        }
      }
    }
  };

  useEffect(() => {
    if (email && !noAuth) {
      if (typeof localStorage !== 'undefined') {
        const user_id_iframe = localStorage.getItem('user_id_iframe');
        if (!user_id_iframe) {
          handleEmailLoginTempIFrame(email as string);
        }
      }
    }
  }, [email, router]);

  const openModal = async () => {
    let session = undefined;
    if (typeof localStorage !== 'undefined') {
      session = localStorage.getItem('user_id_iframe');
    }

    if (!email && pageNumber >= 5 && !noAuth && !session) {
      setVisibleControl(true);
    }
  };

  useEffect(() => {
    openModal();
  }, [email, router, pageNumber]);

  const onOpenDrawer = () => {
    setOpenDrawer(!openDrawer);
    if (screens.lg) {
      if (!openDrawer) {
        setSideBySide(false);
      } else {
        setSideBySide(true);
      }
    }
  };

  return (
    <div className='relative flex ant-layout-pdf h-full'>
      <Layout
        className={clsx(
          ' bg-bgColor h-full relative hide-scrollbar',
          isFullScreen ? ' min-h-[100vh]' : ' min-h-[85vh]',
        )}
        ref={containerRef}
      >
        <PdfReaderHeader
          headerClass={clsx(
            !theme && !isModal
              ? 'bg-bgColor bg-opacity-75 backdrop-filter  backdrop-blur-md '
              : 'bg-bgColor ',

            `   z-40 w-full min-h-[40px] transition-all `,
          )}
          isFullScreen={isFullScreen}
          toggleFullScreen={toggleFullScreen}
          zoomValue={zoomValue}
          zoomOut={zoomOut}
          zoomIn={zoomIn}
          sideBySide={sideBySide}
          title={booksDetails?.title || ''}
          rotateLeft={rotateLeft}
          pageNumber={pageNumber}
          rotateRight={rotateRight}
          toggleSideBySide={toggleSideBySide}
          openDrawer={openDrawer}
        />

        <Content>
          <div
            className={clsx(
              'bg-opacity-75  backdrop-filter hide-scrollbar backdrop-blur-md overflow-auto relative flex min-h-[85vh]',
              isFullScreen ? 'min-h-[100vh]  mt-14 ' : 'min-h-[85vh]',
              theme && !isModal ? 'bg-white' : '',
              isModal && 'bg-transparent backdrop-blur-sm',
            )}
            style={{
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
            }}
          >
            <div className='w-full'>
              <div className='flex relative h-full items-center overflow-hidden hide-scrollbar'>
                <motion.div
                  onContextMenu={(e) => e.preventDefault()}
                  ref={pdfContainerRef}
                  className='w-full flex justify-center items-center h-full hide-scrollbar relative '
                  animate={{
                    x: screens.lg ? (openDrawer ? '-25vw' : '0') : '',
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 100,
                    damping: 20,
                  }}
                >
                  {hasError && !(samplePdf || renderPdf) ? (
                    errorType === 'server-timeout' ? (
                      <FailedToLoadPDF
                        title='Check Your Network'
                        description='Please try again later.'
                        primaryButtonText='Go Back Home'
                        primaryButtonLink='/'
                        secondaryButtonText='Refresh'
                        secondaryButtonLink='/'
                        contactText='Contact Support'
                        contactLink='/support'
                        imageSrc={NetworkError}
                      />
                    ) : (
                      <FailedToLoadPDF
                        title='Something went wrong!'
                        description='Please try again later.'
                        primaryButtonText='Retry'
                        primaryButtonLink='/retry'
                        secondaryButtonText='Home'
                        secondaryButtonLink='/'
                        contactText='Get Support'
                        contactLink='/support'
                        imageSrc={FailedToLoadPdf}
                      />
                    )
                  ) : (
                    <Document
                      className={clsx(
                        'pdf-container w-full h-full overflow-auto z-10 hide-scrollbar',
                      )}
                      renderMode='canvas'
                      file={samplePdf ?? renderPdf}
                      rotate={rotations[pageNumber]}
                      noData={
                        <FailedToLoadPDF
                          title='Something went wrong!'
                          description='Please try again later.'
                          primaryButtonText='Retry'
                          primaryButtonLink='/retry'
                          secondaryButtonText='Home'
                          secondaryButtonLink='/'
                          contactText='Get Support'
                          contactLink='/support'
                          imageSrc={FailedToLoadPdf}
                        />
                      }
                      error={
                        <FailedToLoadPDF
                          title='Something went wrong!'
                          description='Please try again later.'
                          primaryButtonText='Retry'
                          primaryButtonLink='/retry'
                          secondaryButtonText='Home'
                          secondaryButtonLink='/'
                          contactText='Get Support'
                          contactLink='/support'
                          imageSrc={FailedToLoadPdf}
                        />
                      }
                      loading={
                        <LoadingV2
                          loaderColor='white'
                          messages={[
                            'Almost there, thanks for your patience!',
                            'Getting everything set up for you.',
                            'Your content is on its way!',
                            "Just a sec, making sure everything's perfect.",
                            "Hold on, we're almost ready!",
                            "Loading... we'll be with you shortly.",
                            "Thanks for waiting, we're preparing your journey!",
                          ]}
                        />
                      }
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={(error) => {
                        console.error('Document load error:', error);
                        setHasError(true);
                        if (
                          error.message?.toLowerCase().includes('timeout') ||
                          error.message?.toLowerCase().includes('network') ||
                          error.message?.toLowerCase().includes('fetch')
                        ) {
                          setErrorType('server-timeout');
                        } else {
                          setErrorType('default');
                        }
                      }}
                    >
                      <div
                        className={clsx(
                          isFullScreen && 'items-center',
                          'flex justify-center w-full md:items-center overflow-auto h-full hide-scrollbar',
                        )}
                      >
                        <TransformWrapper
                          ref={transformRef}
                          panning={{
                            disabled: false,
                            velocityDisabled: true,
                            excluded: ['exclude-panning'],
                          }}
                          pinch={{ disabled: false }}
                          doubleClick={{ disabled: true }}
                          wheel={{
                            disabled: true,
                          }}
                          centerOnInit
                          centerZoomedOut
                          zoomAnimation={{
                            animationType: 'linear',
                            animationTime: 200,
                          }}
                        >
                          <TransformComponent
                            wrapperClass='overflow-auto hide-scrollbar'
                            contentClass='w-full h-full hide-scrollbar exclude-panning'
                          >
                            {sideBySide ? (
                              <motion.div
                                className='w-full flex justify-center items-center h-full data-no-pan exclude-panning hide-scrollbar'
                                initial={{ x: 0 }}
                                animate={controls}
                              >
                                <div className='flex justify-center items-center relative'>
                                  {[-4, -3, -2, -1, 0, 1, 2, 3, 4].map(
                                    (offset) => {
                                      const actualPageNumber =
                                        pageNumber + offset;

                                      if (
                                        actualPageNumber < 1 ||
                                        actualPageNumber > numPages
                                      )
                                        return null;

                                      const isHidden =
                                        offset !== 0 && offset !== 1;

                                      return (
                                        <motion.div
                                          key={actualPageNumber}
                                          className={`w-full flex relative z-10 ${
                                            offset === 0
                                              ? 'justify-end'
                                              : 'justify-start'
                                          } ${isHidden ? 'hidden' : ''} page`}
                                        >
                                          <Page
                                            pageNumber={actualPageNumber}
                                            scale={scale}
                                            className='bg-cartBgColor'
                                            height={windowHeight as number}
                                            loading={
                                              <div
                                                style={{
                                                  width: Math.floor(
                                                    windowWidth *
                                                      (screens.xl ? 0.4 : 0.5),
                                                  ),
                                                  minHeight: windowHeight,
                                                }}
                                                className='flex justify-center items-center h-full '
                                              >
                                                <Spin />
                                              </div>
                                            }
                                            width={Math.floor(
                                              windowWidth *
                                                (screens.xl ? 0.4 : 0.5),
                                            )}
                                          />

                                          <div
                                            style={{
                                              background:
                                                'linear-gradient(to right, transparent 0%,rgba(0,0,0,0.2) 46%,rgba(0,0,0,0.5) 49%,rgba(0,0,0,0.6) 50%,rgba(0,0,0,0.5) 51%,rgba(0,0,0,0.2) 52%,transparent 100%)',
                                            }}
                                            className={clsx(
                                              offset === 0
                                                ? ' -right-10'
                                                : ' -left-10',
                                              'absolute h-full w-20 z-40',
                                            )}
                                          ></div>
                                        </motion.div>
                                      );
                                    },
                                  )}
                                </div>
                              </motion.div>
                            ) : (
                              <motion.div
                                className='overflow-auto exclude-panning w-full h-full hide-scrollbar'
                                initial={{ x: 0, y: 0 }}
                                animate={controls}
                                style={{
                                  overflow: 'auto',
                                  height: '100%',
                                  width: '100%',
                                }}
                              >
                                <div className='flex items-center'>
                                  {[-2, -1, 0, 1, 2].map((offset) => {
                                    const actualPageNumber =
                                      pageNumber + offset;

                                    if (
                                      actualPageNumber < 1 ||
                                      actualPageNumber > numPages
                                    )
                                      return null;

                                    const isHidden = offset !== 0;

                                    return (
                                      <Page
                                        key={actualPageNumber}
                                        pageNumber={actualPageNumber}
                                        scale={scale}
                                        height={
                                          screens.lg
                                            ? windowHeight
                                            : isLandscapeMode
                                              ? windowHeight
                                              : undefined
                                        }
                                        width={
                                          screens.lg
                                            ? isLandscapeMode
                                              ? Math.floor(windowWidth * 0.7)
                                              : Math.floor(windowWidth * 0.35)
                                            : windowWidth
                                        }
                                        className={`bg-cartBgColor ${isHidden ? 'hidden' : ''}`}
                                      />
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </TransformComponent>
                        </TransformWrapper>
                      </div>
                    </Document>
                  )}
                  <SelectionTooltip
                    ignoreRefs={[
                      pageNavigatorRef,
                      pageInputRef,
                      takeNotePanelRef,
                    ]}
                    parentRef={pdfContainerRef}
                    setTakeNodeModal={setTakeNoteModal}
                    takeNoteModal={takeNoteModal}
                  />
                </motion.div>
              </div>
            </div>
            <ChapterDrawer
              visible={visible}
              onClose={onClose}
              samplePdf={!!samplePdf as boolean}
              book_id={book_id as string}
              baseUrl={baseUrl as string}
              Endpoints={Endpoints}
              booksDetails={booksDetails}
              pageNumber={pageNumber}
              numPages={numPages}
              chaptersList={chaptersList}
              chapterFieldId={chapterFieldId}
              onChapterHandler={onChapterHandler}
              isFullScreen={isFullScreen}
            />

            <NotesApp
              landingPageUrl={
                `${
                  process.env.NEXT_PUBLIC_APP_ENV === 'production'
                    ? 'https://zebralearn.com'
                    : 'https://staging.zebralearn.com'
                }/books/${booksDetails?.slug}-${book_id}` as string
              }
              bookId={book_id as string}
              bookAuthor={booksDetails?.author_list
                ?.map((author) => author.name)
                .join(', ')}
              bookTitle={booksDetails?.title as string}
            />

            <div className='flex items-center'>
              {' '}
              <AiChatDrawer
                onCancel={onCloseDrawer}
                visible={openDrawer}
                isFullScreen={isFullScreen}
                bookData={{
                  name: booksDetails?.title as string,
                  image: `${baseUrl}/${Endpoints.getCoverImagesForBooks}/${book_id}`,
                }}
              />
            </div>
          </div>
        </Content>
        <ShouldRender
          check={
            (!openDrawer || screens.lg) &&
            (samplePdf ? numPages || 0 : numPages || 0) > 1 &&
            !drawerState.contentDrawer &&
            !drawerState.notesDrawer
          }
        >
          <PageNavigator
            pageNumber={pageNumber}
            numPages={numPages}
            setPageNumber={setPageNumber}
            getNextPage={getNextPage}
            getPreviousPage={getPreviousPage}
            leftButtonRef={leftButtonRef}
            rightButtonRef={rightButtonRef}
            wrapperRef={pageNavigatorRef}
          />
        </ShouldRender>
        <ShouldRender check={drawerState.notesDrawer && isFullScreen}>
          <div
            className={clsx(
              'fixed md:hidden bottom-0 left-0  z-50',
              'w-full lg:pb-5',
            )}
          >
            <div className='bg-bgColor border-t-[1px] lg:border-none border-[#2A2A2A] md:px-6 items-center translate-y-1 p-2 xs:p-3 lg:p-0  flex'>
              <ButtonDefault
                size={2}
                onClick={handleDownload}
                variant={ButtonVariants.WHITE}
                className='w-full  whitespace-nowrap'
              >
                <div className='py- px-4'>Download as PDF</div>
              </ButtonDefault>
            </div>
          </div>
        </ShouldRender>
      </Layout>
      <ShouldRender
        check={
          !openDrawer ||
          (isFullScreen &&
            !drawerState.contentDrawer &&
            !drawerState.notesDrawer)
        }
      >
        <div className='h-full'>
          <ToolBoxSidebar
            fullScreenIcon={fullScreenIcon}
            isFullScreen={isFullScreen}
            isIphone={isIphone}
            showDrawer={showDrawer}
            toggleFullScreen={toggleFullScreen}
            userData={userData as User}
            visible={visible}
            key={`toolbox-${book_id}`}
            onOpenDrawer={onOpenDrawer}
            book_id={book_id as string}
            hideAudioBook={
              !!(
                booksDetails?.completeBookAudio &&
                booksDetails?.completeBookAudio !== 'null'
              )
            }
            isIframe={true}
          />
        </div>
      </ShouldRender>
      <TakeNotePanel
        open={takeNoteModal.value}
        onCancel={() => {
          setTakeNoteModal({
            value: false,
            pageNumber: 0,
            selectedText: '',
            selectionRect: null,
          });
          if (typeof window !== 'undefined') {
            const selection = window.getSelection?.();
            if (selection?.rangeCount) selection.removeAllRanges();
          }
        }}
        takeNotePanelRef={takeNotePanelRef}
        pdfUrl={samplePdf ?? renderPdf}
        book_id={book_id as string}
        chapterNumber={useCurrentChapter?.chapterNumber}
        pageNumber={takeNoteModal.pageNumber || 1}
        selectedText={takeNoteModal.selectedText}
        containerHeight={windowHeight as number}
        containerWidth={windowWidth as number}
        chapterId={useCurrentChapter?._id}
        coordinates={takeNoteModal.selectionRect}
        isFullScreen={isFullScreen}
      />
    </div>
  );
};

export default PDFReaderIframe;
